import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api from "../api/api";

interface User { id: string; name: string; email: string; role: string; department?: string; isActive?: boolean; }
interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  allowedTabs: string[];
  permissions: any[];
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAdmin: boolean;
  isManager: boolean;
  isDeptHead: boolean;
  canManageUsers: boolean;
  canAccessTab: (tab: string) => boolean;
  refetchPermissions: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

const POLL_INTERVAL = 15000; // 15 seconds

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser]             = useState<User | null>(null);
  const [allowedTabs, setAllowedTabs] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<any[]>([]);
  const [isLoading, setIsLoading]   = useState(true);
  const permVersionRef = useRef<number>(0);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const u = localStorage.getItem("user");
    // Only restore session if BOTH token and user exist
    if (token && u) {
      try {
        const parsed = JSON.parse(u);
        setUser(parsed);
        const tabs = localStorage.getItem("allowedTabs");
        if (tabs) setAllowedTabs(JSON.parse(tabs));
        const perms = localStorage.getItem("permissions");
        if (perms) setPermissions(JSON.parse(perms));
      } catch { localStorage.clear(); }
    } else {
      // Token or user missing — clear stale data
      localStorage.clear();
    }
    setIsLoading(false);
  }, []);

  const refetchPermissions = useCallback(async () => {
    const currentUser = JSON.parse(localStorage.getItem("user") || "null");
    const token = localStorage.getItem("token");
    if (!currentUser || !token) return;

    try {
      // Fetch tab permissions
      const tabRes = await api.get(`/users/${currentUser.id}/tab-permissions`);
      const tabs = tabRes.data?.length ? tabRes.data : [];
      localStorage.setItem("allowedTabs", JSON.stringify(tabs));
      setAllowedTabs(tabs);

      // Fetch role permissions
      const permRes = await api.get(`/permissions/${currentUser.role}`);
      const perms = permRes.data || [];
      localStorage.setItem("permissions", JSON.stringify(perms));
      setPermissions(perms);

      console.log(`Permissions updated live for user ${currentUser.name}`);
    } catch (err) {
      // Silently fail — will retry on next poll
    }
  }, []);

  // Poll for permission changes
  useEffect(() => {
    if (!user) return;

    const checkForUpdates = async () => {
      try {
        const res = await api.get("/permissions/version");
        const serverVersion = res.data?.version || 0;
        if (permVersionRef.current > 0 && serverVersion !== permVersionRef.current) {
          // Version changed — refetch full permissions
          await refetchPermissions();
        }
        permVersionRef.current = serverVersion;
      } catch {
        // Silently fail
      }
    };

    // Initial version check
    checkForUpdates();

    const interval = setInterval(checkForUpdates, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [user, refetchPermissions]);

  const login = async (email: string, password: string) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", res.data.access_token);
    localStorage.setItem("user", JSON.stringify(res.data.user));
    setUser(res.data.user);
    try {
      const tabRes = await api.get(`/users/${res.data.user.id}/tab-permissions`, {
        headers: { Authorization: `Bearer ${res.data.access_token}` }
      });
      const tabs = tabRes.data?.length ? tabRes.data : [];
      localStorage.setItem("allowedTabs", JSON.stringify(tabs));
      setAllowedTabs(tabs);
    } catch { setAllowedTabs([]); }
    try {
      const permRes = await api.get(`/permissions/${res.data.user.role}`, {
        headers: { Authorization: `Bearer ${res.data.access_token}` }
      });
      const perms = permRes.data || [];
      localStorage.setItem("permissions", JSON.stringify(perms));
      setPermissions(perms);
    } catch { setPermissions([]); }

    // Set initial version
    try {
      const vRes = await api.get("/permissions/version");
      permVersionRef.current = vRes.data?.version || 0;
    } catch {}
  };

  const logout = () => { localStorage.clear(); setUser(null); setAllowedTabs([]); setPermissions([]); permVersionRef.current = 0; window.location.href = "/login"; };

  const isAdmin    = user?.role === "ADMIN";
  const isDeptHead = user?.role === "DEPARTMENT_HEAD" || isAdmin;
  const isManager  = user?.role === "MANAGER" || isDeptHead;
  const canManageUsers = isAdmin;

  const canAccessTab = (tab: string) => {
    if (isAdmin) return true;
    // Check tab-level permissions (set per user)
    const tabAllowed = !allowedTabs || allowedTabs.length === 0 || allowedTabs.includes(tab);
    // Check role-level permissions (canView from RolePermission)
    const rolePerm = permissions?.find((p: any) => p.module === tab);
    const roleAllowed = rolePerm ? !!rolePerm.canView : false;
    // Tab is accessible if allowed by EITHER tab permissions or role permissions
    return tabAllowed || roleAllowed;
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, allowedTabs, permissions, login, logout, isAdmin, isManager, isDeptHead, canManageUsers, canAccessTab, refetchPermissions }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
